import { getCurrencyFormat, zero } from '@helpers/currency';
import { useGetTenantQuery } from 'api/tenant.api';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import { makeStyles } from '@mui/styles';
import { ThemedSecondaryBtn } from '@themes/defaultThemeProps';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { toast } from 'react-toastify';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
}));

export default function Receipt(props) {
  const { data: tenant } = useGetTenantQuery();
  const currencyFormat = getCurrencyFormat(tenant?.base_currency);
  const receipt = props?.receipt?.receipt_data;

  const buildItemAttributes = (attributes) => {
    const attr = attributes.map((atr) => {
      return (
        <>
          <GridItem xs={2}></GridItem>
          <GridItem xs={1}>{atr.label}:</GridItem>
          <GridItem xs={3}>{atr.value}</GridItem>
          <GridItem xs={6}></GridItem>
        </>
      );
    });
    return attr;
  };

  const buildPriceModifiers = (modifiers) => {
    const mods = modifiers.map((mod) => {
      return (
        <>
          <GridItem xs={2}></GridItem>
          <GridItem xs={4}>{mod.description}</GridItem>
          <GridItem xs={3}></GridItem>
          <GridItem xs={3}>{mod.amount}</GridItem>
        </>
      );
    });
    return mods;
  };

  const buildLineItem = (item) => {
    return (
      <>
        <GridItem xs={2}>{item.item_id}</GridItem>
        <GridItem xs={4}>{item.description}</GridItem>
        <GridItem xs={1}>{item.quantity ? item.quantity : '1'}</GridItem>
        <GridItem
          xs={2}
          container
          direction='row'
          justifyContent='flex-end'
          alignItems='flex-start'
        >
          {item.price}
        </GridItem>
        <GridItem
          xs={3}
          container
          direction='row'
          justifyContent='flex-end'
          alignItems='flex-start'
        >
          {item.extended_price}
        </GridItem>
        {buildItemAttributes(item.attributes)}
        {buildPriceModifiers(item.price_modifiers)}
      </>
    );
  };

  const buildLineItems = () => {
    let lineItems = [];
    // for each line item
    if (!receipt) {
      toast.error('No receipt data available');
      return;
    }

    if (receipt?.cart) lineItems = receipt.cart?.line_items;
    else lineItems = receipt?.line_items;
    const items = lineItems.map((item) => {
      return buildLineItem(item);
    });
    return items;
  };

  const buildPOAChargeCard = (card) => {
    return (
      <>
        <GridItem xs={1}></GridItem>
        <GridItem xs={2}>
          Company name:
          <br />
          Account number:
          <br />
          Authorized user:
          <br />
        </GridItem>
        <GridItem xs={2}>
          {card?.company_name}
          <br />
          {card?.account_number}
          <br />
          {card?.authorized_user_id}
        </GridItem>
        <GridItem xs={7}></GridItem>
      </>
    );
  };

  const buildChargeCard = (card) => {
    return (
      <>
        <GridItem xs={2}></GridItem>
        <GridItem xs={4}>
          processor id:
          <br />
          last 4:
          <br />
          brand:
          <br />
          account type:
          <br />
          application preferred name:
          <br />
          dedicated file name:
          <br />
          authorization response code:
          <br />
          application cryptogram:
          <br />
          terminal verification results:
          <br />
          transaction status information:
          <br />
        </GridItem>
        <GridItem xs={4}>
          {card?.processor_id}
          <br />
          {card?.last4}
          <br />
          {card?.brand}
          <br />
          {card?.account_type}
          <br />
          {card?.application_preferred_name}
          <br />
          {card?.dedicated_file_name}
          <br />
          {card?.authorization_response_code}
          <br />
          {card?.application_cryptogram}
          <br />
          {card?.terminal_verification_results}
          <br />
          {card?.transaction_status_information}
          <br />
        </GridItem>
        <GridItem xs={2}></GridItem>
      </>
    );
  };

  const buildWallet = (wallet) => {
    return (
      <>
        <GridItem xs={2}></GridItem>
        <GridItem xs={4}>
          method:
          <br />
          transaction id:
          <br />
          merchant id:
          <br />
          terminal id:
          <br />
        </GridItem>
        <GridItem xs={2}>
          {wallet?.method}
          <br />
          {wallet?.transaction_id}
          <br />
          {wallet?.merchant_id}
          <br />
          {wallet?.terminal_id}
          <br />
        </GridItem>
        <GridItem xs={4}></GridItem>
      </>
    );
  };

  const buildVoucher = (voucher) => {
    return (
      <>
        <GridItem xs={2}></GridItem>
        <GridItem xs={4}>
          last 4:
          <br />
          type:
          <br />
        </GridItem>
        <GridItem xs={2}>
          {voucher?.last4}
          <br />
          {voucher?.type}
          <br />
        </GridItem>
        <GridItem xs={6}></GridItem>
      </>
    );
  };

  const buildTenderItem = (item) => {
    return (
      <>
        <GridItem xs={2}>
          {item?.type === 'POA' ? 'Pay On Account' : item?.type}
        </GridItem>
        <GridItem xs={4}>
          <b>{item?.amount}</b>
        </GridItem>
        {item?.type === 'Cash' &&
        item?.cash?.roundedRemainder &&
        item?.cash?.roundedRemainder !== zero.toFormat(currencyFormat) ? (
          <>
            <GridItem xs={6}></GridItem>
            <GridItem xs={2}>Cash Rounding</GridItem>
            <GridItem xs={4}>{item?.cash?.roundedRemainder}</GridItem>
          </>
        ) : null}
        <GridItem xs={6}></GridItem>
        {item?.poa ? buildPOAChargeCard(item?.poa) : null}
        {item?.charge_card ? buildChargeCard(item?.charge_card) : null}
        {item?.voucher ? buildVoucher(item?.voucher) : null}
        {item?.wallet ? buildWallet(item?.wallet) : null}
      </>
    );
  };

  const buildTenders = () => {
    let tenders = null;
    // for each line item
    if (receipt?.refunds?.length > 0)
      tenders = receipt?.refunds?.map((item) => {
        return buildTenderItem(item);
      });
    else if (receipt?.payments)
      tenders = receipt?.payments?.map((item) => {
        return buildTenderItem(item);
      });
    return tenders;
  };

  return (
    <div>
      <Dialog
        open={props.open}
        onClose={props.handleClose}
        aria-labelledby='form-dialog-title'
        maxWidth='lg'
      >
        <DialogTitle id='form-dialog-title'>Transaction Receipt</DialogTitle>

        <DialogContent>
          <GridContainer>
            <GridItem xs={1}>
              Order:
              <br />
              {props.serviceType !== 'cashmanagement' ? (
                <>
                  MIST ID:
                  <br />
                </>
              ) : null}
              Date:
              <br />
              Workstation:
              <br />
              Cashier:
              <br />
            </GridItem>
            <GridItem xs={5}>
              {receipt?.order_number} <br />
              {props.serviceType !== 'cashmanagement' ? (
                <>
                  {props?.receipt?.session_id}
                  <br />
                </>
              ) : null}
              {new Date(receipt?.timestamp).toLocaleString()} <br />
              {receipt?.workstation_id} <br />
              {receipt?.user_id} <br />
            </GridItem>
            <GridItem xs={6}>
              {receipt?.location.location_name}, {receipt?.location.location_id}{' '}
              <br />
              {receipt?.location.description
                ? receipt?.location.description
                : null}{' '}
              <br />
              {receipt?.location.address_line_1} <br />
              {receipt?.location.address_line_2} <br />
              {receipt?.location.city}, {receipt?.location.state}{' '}
              {receipt?.location.postal_code} <br />
              {receipt?.location.telephone_number}
            </GridItem>
            <GridItem xs={12}>
              <Divider light />
            </GridItem>
            <GridItem xs={2}>
              <h6>Item #</h6>
            </GridItem>
            <GridItem xs={4}>
              <h6>Description</h6>
            </GridItem>
            <GridItem xs={1}>
              <h6>Quantity</h6>
            </GridItem>
            <GridItem
              xs={2}
              container
              direction='row'
              justifyContent='flex-end'
              alignItems='flex-start'
            >
              <h6>Unit Price</h6>
            </GridItem>
            <GridItem
              xs={3}
              container
              direction='row'
              justifyContent='flex-end'
              alignItems='flex-start'
            >
              <h6>Extended Price</h6>
            </GridItem>
            {buildLineItems()}
            <GridItem xs={12}>
              <Divider light />
            </GridItem>
            <GridItem xs={1} style={{ marginLeft: 20 }}>
              <GridItem
                xs={12}
                container
                direction='row'
                justifyContent='flex-end'
                alignItems='flex-start'
              >
                <GridItem>Subtotal:</GridItem>
              </GridItem>
              <GridItem
                xs={12}
                container
                direction='row'
                justifyContent='flex-end'
                alignItems='flex-start'
              >
                <GridItem>Tax:</GridItem>
              </GridItem>
              <GridItem
                xs={12}
                container
                direction='row'
                justifyContent='flex-end'
                alignItems='flex-start'
              >
                <GridItem>Total:</GridItem>
              </GridItem>
            </GridItem>
            <GridItem xs={1}>
              <GridItem
                xs={12}
                container
                direction='row'
                justifyContent='flex-end'
                alignItems='flex-start'
              >
                <GridItem>{receipt?.subtotal}</GridItem>
              </GridItem>
              <GridItem
                xs={12}
                container
                direction='row'
                justifyContent='flex-end'
                alignItems='flex-start'
              >
                <GridItem>{receipt?.tax}</GridItem>
              </GridItem>
              <GridItem
                xs={12}
                container
                direction='row'
                justifyContent='flex-end'
                alignItems='flex-start'
              >
                <GridItem>{receipt?.total}</GridItem>
              </GridItem>
            </GridItem>
            <GridItem xs={12}>
              <Divider light />
            </GridItem>
            <GridItem xs={2}>
              <h6>
                {receipt?.refund_tenders?.length > 0
                  ? 'Refund Tender Type'
                  : 'Tender Type'}
              </h6>
            </GridItem>
            <GridItem xs={4}>
              <h6>Amount</h6>
            </GridItem>
            <GridItem xs={6}></GridItem>
            {buildTenders()}
          </GridContainer>
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn onClick={props.handleClose}>
            Close
          </ThemedSecondaryBtn>
        </DialogActions>
      </Dialog>
    </div>
  );
}
